import PageLayout from '../../components/sportsbook-page-layout';
import { PromotionCarouselContainer } from '../../sportsbook-async';
import SportsbookExploreQuickLinks from './components/sportsbook-explore-quick-links';
import { isSmallerSelector } from '../../../../layout/mediaquery';
import { ColumnLayoutContainer, ColumnWrapper } from '../../components/layout/sportsbook-column-layout';
import { useTypedTranslations } from '../../../../translation/use-translations';
import SportsbookTextKeys from '../../sportsbook-text-keys';
import {
	SportsbookExploreHotLiveContent,
	SportsbookExploreHotUpcomingContent,
	SportsbookExploreLiveContent,
	SportsbookExploreUpcomingContent
} from './components/sportsbook-explore-featured-content';
import styled from 'styled-components';
import ExploreTextKeys from './sportsbook-explore-text-keys';
import SportsbookExploreSeoContainer from './sportsbook-explore-seo-container';
import useScrollToTop from '../../../../../hooks/use-scroll-to-top';
import useQueryFeaturedContent from '../../queries/featured-content/use-query-featured-content';
import { FeaturedContentDto, FeaturedContentTypeDto } from '../../api/sbapi/sbapi-api-model';
import { RefetchInterval, StaleTime } from '../../sportsbook-constants';
import SportsbookExploreRecommendedCasinoGames from './components/sportsbook-explore-recommended-casino-games';
import SportsbookExploreRecommendedBets from './components/sportsbook-explore-recommended-bets';
import SportsbookExploreRecommendedPreCanneds from './components/sportsbook-explore-recommended-pre-canneds';
import {
	selectSportsbookBetRecommendationsOnExplorePageEnabled,
	selectSportsbookPreCannedCarouselEnabled,
	selectSportsbookPreCannedCarouselPositionBottomPageEnabled,
	selectIsDefaultFranchiseLanguageSelected
} from '../../../../brand/ducks/selectors';
import { useAppSelector } from '~/store/types';

const StyledColumnLayoutContainer = styled(ColumnLayoutContainer)<{ hasColumnDirection?: boolean }>`
	flex-direction: ${({ hasColumnDirection }) => (hasColumnDirection ? 'column' : 'row')};
`;

const hasContent = (content?: FeaturedContentDto): content is FeaturedContentDto =>
	!!content && content.filters.length > 0;

/**
 * Prefetch all required featured content. Return if booleans if have content, together with query status.
 */
const useExploreFeaturedContent = () => {
	const { data, isLoading } = useQueryFeaturedContent(
		[
			FeaturedContentTypeDto.LIVE,
			FeaturedContentTypeDto.UPCOMING,
			FeaturedContentTypeDto.HOT_LIVE,
			FeaturedContentTypeDto.HOT_UPCOMING
		],
		{
			staleTime: StaleTime.THIRTY_SECONDS,
			refetchInterval: RefetchInterval.SHORT,
			select: list => {
				const liveContent = list.find(featuredContent => featuredContent.type === FeaturedContentTypeDto.LIVE);
				const upcomingContent = list.find(
					featuredContent => featuredContent.type === FeaturedContentTypeDto.UPCOMING
				);
				const hotLiveContent = list.find(
					featuredContent => featuredContent.type === FeaturedContentTypeDto.HOT_LIVE
				);
				const hotUpcomingContent = list.find(
					featuredContent => featuredContent.type === FeaturedContentTypeDto.HOT_UPCOMING
				);
				return {
					liveContent,
					upcomingContent,
					hotLiveContent,
					hotUpcomingContent
				};
			}
		}
	);

	return {
		liveContent: data?.liveContent,
		upcomingContent: data?.upcomingContent,
		hotLiveContent: data?.hotLiveContent,
		hotUpcomingContent: data?.hotUpcomingContent,
		isLoading
	};
};

const SportsbookExplorePageContainer = () => {
	const isDefaultFranchiseLanguageSelected = useAppSelector(selectIsDefaultFranchiseLanguageSelected);
	const isPreCannedCarouselEnabled = useAppSelector(selectSportsbookPreCannedCarouselEnabled);
	const isPreCannedCarouselPositionBottomPageEnabled = useAppSelector(
		selectSportsbookPreCannedCarouselPositionBottomPageEnabled
	);
	const isRecommendedBetsCarouselEnabled = useAppSelector(selectSportsbookBetRecommendationsOnExplorePageEnabled);
	const isSmaller = useAppSelector(isSmallerSelector);
	const { translations, isLoadingTranslations } = useTypedTranslations({ ...SportsbookTextKeys, ...ExploreTextKeys });
	useScrollToTop();

	const {
		liveContent,
		upcomingContent,
		hotLiveContent,
		hotUpcomingContent,
		isLoading: isLoadingFeaturedContentFilters
	} = useExploreFeaturedContent();

	if (isLoadingTranslations) {
		return null;
	}

	const hasLiveContent = hasContent(liveContent);
	const hasUpcomingContent = hasContent(upcomingContent);
	const hasHotLiveContent = hasContent(hotLiveContent);
	const hasHotUpcomingContent = hasContent(hotUpcomingContent);

	if (isSmaller) {
		return (
			<PageLayout>
				<SportsbookExploreSeoContainer />
				{isDefaultFranchiseLanguageSelected && <PromotionCarouselContainer />}
				<SportsbookExploreQuickLinks />
				{isPreCannedCarouselEnabled && !isPreCannedCarouselPositionBottomPageEnabled && (
					<SportsbookExploreRecommendedPreCanneds translations={translations} />
				)}
				{isRecommendedBetsCarouselEnabled && <SportsbookExploreRecommendedBets translations={translations} />}
				<StyledColumnLayoutContainer hasColumnDirection>
					{!isLoadingFeaturedContentFilters && (
						<ColumnWrapper>
							{hasHotLiveContent && (
								<SportsbookExploreHotLiveContent
									featuredContent={hotLiveContent}
									translations={translations}
								/>
							)}
							{hasHotUpcomingContent && (
								<SportsbookExploreHotUpcomingContent
									featuredContent={hotUpcomingContent}
									translations={translations}
								/>
							)}
							{isPreCannedCarouselEnabled && isPreCannedCarouselPositionBottomPageEnabled && (
								<SportsbookExploreRecommendedPreCanneds translations={translations} isInColumn />
							)}
							<SportsbookExploreRecommendedCasinoGames translations={translations} />
							{hasLiveContent && (
								<SportsbookExploreLiveContent
									featuredContent={liveContent}
									translations={translations}
								/>
							)}
							{hasUpcomingContent && (
								<SportsbookExploreUpcomingContent
									featuredContent={upcomingContent}
									translations={translations}
								/>
							)}
						</ColumnWrapper>
					)}
				</StyledColumnLayoutContainer>
			</PageLayout>
		);
	}

	return (
		<PageLayout>
			<SportsbookExploreSeoContainer />
			{isDefaultFranchiseLanguageSelected && <PromotionCarouselContainer />}
			{isPreCannedCarouselEnabled && !isPreCannedCarouselPositionBottomPageEnabled && (
				<SportsbookExploreRecommendedPreCanneds translations={translations} />
			)}
			{isRecommendedBetsCarouselEnabled && <SportsbookExploreRecommendedBets translations={translations} />}
			<StyledColumnLayoutContainer>
				{!isLoadingFeaturedContentFilters && (
					<>
						<ColumnWrapper>
							{hasHotLiveContent && (
								<SportsbookExploreHotLiveContent
									featuredContent={hotLiveContent}
									translations={translations}
								/>
							)}
							{isPreCannedCarouselEnabled &&
								isPreCannedCarouselPositionBottomPageEnabled &&
								hasHotLiveContent && (
									<SportsbookExploreRecommendedPreCanneds translations={translations} isInColumn />
								)}
							{hasHotLiveContent && (
								<SportsbookExploreRecommendedCasinoGames translations={translations} />
							)}
							{hasLiveContent && (
								<SportsbookExploreLiveContent
									featuredContent={liveContent}
									translations={translations}
								/>
							)}
							{isPreCannedCarouselEnabled &&
								isPreCannedCarouselPositionBottomPageEnabled &&
								!hasHotLiveContent && (
									<SportsbookExploreRecommendedPreCanneds translations={translations} isInColumn />
								)}
							{!hasHotLiveContent && (
								<SportsbookExploreRecommendedCasinoGames translations={translations} />
							)}
						</ColumnWrapper>
						<ColumnWrapper>
							{hasHotUpcomingContent && (
								<SportsbookExploreHotUpcomingContent
									featuredContent={hotUpcomingContent}
									translations={translations}
								/>
							)}
							{hasUpcomingContent && (
								<SportsbookExploreUpcomingContent
									featuredContent={upcomingContent}
									translations={translations}
								/>
							)}
						</ColumnWrapper>
					</>
				)}
			</StyledColumnLayoutContainer>
		</PageLayout>
	);
};

export default SportsbookExplorePageContainer;
